import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Sparkles, Copy, RefreshCw, ExternalLink, Wrench, TrendingUp, MessageCircle,
  CheckCircle2, AlertTriangle, Clock, Camera, Upload, Trash2, Globe,
} from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

const enc = encodeURIComponent;
const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const PLATFORMS = [
  { id: 'marktplaats', naam: 'Marktplaats', kleur: '#3f9c35' },
  { id: 'facebook', naam: 'Facebook', kleur: '#1877f2' },
  { id: 'forum', naam: 'Forum', kleur: '#7c3aed' },
];

export default function PartAdvertentie({ part, onClose, onUseText, onNeedSave }) {
  const { t, lang } = useT();
  const { gegevens } = useData();
  const showroomUrl = gegevens?.showroomSlug ? `${window.location.origin}/showroom/${gegevens.showroomSlug}` : '';
  const adSuffix = (plat) => (gegevens?.showroomLinkInAds && showroomUrl && plat !== 'marktplaats')
    ? `\n\n${gegevens.showroomAdCta || 'Meer onderdelen & occasions nodig? Bekijk mijn complete aanbod in mijn online showroom:'}\n${showroomUrl}`
    : '';
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [platform, setPlatform] = useState('marktplaats');
  const [tab, setTab] = useState('tekst'); // tekst | foto | prijs
  const [stijl, setStijl] = useState('standaard');
  const [fotos, setFotos] = useState([]); // [{dataUrl}]
  const [showroom, setShowroom] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const fileRef = React.useRef(null);

  const STIJLEN = [
    { id: 'standaard', label: t('Standaard', 'Standaard') },
    { id: 'zakelijk', label: t('Zakelijk', 'Zakelijk') },
    { id: 'enthousiast', label: t('Enthousiast', 'Enthousiast') },
    { id: 'kort', label: t('Kort', 'Kort') },
  ];

  const term = useMemo(() => [part.naam, part.afkomstigVoertuig, part.oemNummer].filter(Boolean).join(' ').trim(), [part]);

  // Bestaande foto's van het onderdeel vooraf laden
  useEffect(() => {
    if (Array.isArray(part.fotos) && part.fotos.length) setFotos(part.fotos.map(f => ({ dataUrl: f })));
    if (part.id) carsysApi.getShowroomStatusForBron(part.id).then(r => { if (r && r.status) setShowroom(r); }).catch(() => {});
  }, [part]);

  const genereer = async () => {
    setLoading(true);
    try {
      const r = await carsysApi.generatePartAdvertentie({
        ...part, prijs: part.vraagPrijs, stijl, lang,
        fotos: fotos.map(f => f.dataUrl),
      });
      setResult(r);
    } catch (e) {
      toast.error(t('Genereren mislukt', 'Genereren mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { genereer(); /* eslint-disable-next-line */ }, []);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - fotos.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => setFotos(prev => [...prev, { dataUrl: reader.result }]);
      reader.readAsDataURL(file);
    });
    if (fileRef.current) fileRef.current.value = '';
  };
  const delFoto = (i) => setFotos(prev => prev.filter((_, idx) => idx !== i));

  const huidig = result?.platforms?.[platform];
  const kopieer = (txt) => { navigator.clipboard?.writeText(txt || ''); toast.success(t('Gekopieerd', 'Gekopieerd')); };
  const kopieerAlles = () => { if (huidig) kopieer(`${huidig.titel}\n\n${huidig.tekst}${adSuffix(platform)}`); };

  // Foto's exporteren (downloaden) zodat je ze op Facebook/Marktplaats kunt uploaden
  const downloadFotos = () => {
    if (!fotos.length) { toast.info(t('Geen foto\'s om te downloaden', 'Geen foto\'s om te downloaden')); return; }
    const basis = (part.naam || 'onderdeel').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase() || 'onderdeel';
    fotos.forEach((f, i) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = f.dataUrl;
        a.download = `${basis}_${i + 1}.jpg`;
        document.body.appendChild(a); a.click(); a.remove();
      }, i * 600);
    });
    toast.success(t('Foto\'s gedownload', 'Foto\'s gedownload'), { description: `${fotos.length} ${t('foto(\'s) opgeslagen', 'foto(\'s) opgeslagen')}` });
  };

  // "Plaats op Facebook": kopieert de hele advertentie, downloadt de foto's en opent FB Marketplace.
  const plaatsOpFacebook = () => {
    const fb = result?.platforms?.facebook;
    const titel = fb?.titel || part.naam || '';
    const tekst = fb?.tekst || '';
    const prijs = Number(part.vraagPrijs) > 0 ? `\n\n${t('Prijs', 'Prijs')}: ${eur(part.vraagPrijs)}` : '';
    navigator.clipboard?.writeText(`${titel}\n\n${tekst}${prijs}`.trim());
    if (fotos.length) downloadFotos();
    window.open('https://www.facebook.com/marketplace/create/item', '_blank', 'noopener');
    toast.success(t('Klaar om te plakken op Facebook', 'Klaar om te plakken op Facebook'), {
      description: t('Titel + tekst staan op je klembord; foto\'s zijn gedownload. Plak ze in Facebook Marketplace.', 'Titel + tekst staan op je klembord; foto\'s zijn gedownload. Plak ze in Facebook Marketplace.'),
    });
  };

  const publiceer = async () => {
    setPublishing(true);
    try {
      let bronId = part.id;
      if (!bronId && onNeedSave) { bronId = await onNeedSave(); }
      if (!bronId) { toast.error(t('Sla het onderdeel eerst op', 'Sla het onderdeel eerst op')); setPublishing(false); return; }
      const r = await carsysApi.publishShowroom({
        type: 'onderdeel', bronId, status: 'Gepubliceerd',
        titel: part.naam, naam: part.naam, prijs: part.vraagPrijs,
        conditie: part.conditie, aantal: part.aantal, oemNummer: part.oemNummer,
        onderdeelNummer: part.onderdeelNummer, afkomstigVoertuig: part.afkomstigVoertuig,
        bouwjaar: part.bouwjaar, kmStand: part.kmStand, motor: part.motor, kenteken: part.kenteken,
        pastOp: part.pastOp || [], oemCrossRef: part.oemCrossRef || [],
        categorie: part.categorie, aiTekst: huidig?.tekst || part.opmerkingen || '',
        fotos: fotos.map(f => f.dataUrl),
      });
      setShowroom(r);
      toast.success(t('Gepubliceerd in showroom!', 'Gepubliceerd in showroom!'), { description: r.publicUrl });
    } catch (e) {
      toast.error(t('Publiceren mislukt', 'Publiceren mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setPublishing(false); }
  };

  const mp = result?.marktprijs;
  const vk = result?.verkoopkans;
  const fa = result?.fotoanalyse;
  const winst = result?.winst;
  const marktplaatsUrl = `https://www.marktplaats.nl/q/${enc(term || part.naam)}/`;

  return (
    <div className="cs-modal-overlay" onClick={onClose} data-testid="part-advertentie-modal">
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, width: '96vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Sparkles size={20} color="#7c3aed" />
            <div><h3 style={{ margin: 0 }}>{t('AI Onderdeel-advertentie', 'AI Onderdeel-advertentie')}</h3>
            <div style={{ fontSize: 13, color: 'var(--cs-text-muted)' }}>{part.naam} {part.conditie ? `· ${part.conditie}` : ''}</div></div>
          </div>
          <button onClick={onClose} data-testid="pa-close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cs-text-muted)' }}><X size={20} /></button>
        </div>

        {/* Stijl + opnieuw */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', fontWeight: 600 }}>{t('Stijl', 'Stijl')}:</span>
          {STIJLEN.map(s => (
            <button key={s.id} onClick={() => setStijl(s.id)} data-testid={`pa-stijl-${s.id}`}
              className={`cs-btn ${stijl === s.id ? 'primary' : ''}`} style={{ padding: '5px 11px', fontSize: 12.5 }}>{s.label}</button>
          ))}
          <button onClick={genereer} disabled={loading} className="cs-btn" data-testid="pa-regenereer" style={{ marginLeft: 'auto' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> {loading ? t('Bezig…', 'Bezig…') : t('Opnieuw genereren', 'Opnieuw genereren')}
          </button>
        </div>

        {loading && !result ? (
          <div className="cs-empty" data-testid="pa-loading" style={{ padding: '50px 20px' }}>
            <Sparkles size={26} color="#7c3aed" /><div style={{ marginTop: 10 }}>{t('AI schrijft je advertentie…', 'AI schrijft je advertentie…')}</div>
          </div>
        ) : result ? (
          <>
            {/* Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 14 }} data-testid="pa-metrics">
              {mp && <Metric icon={<TrendingUp size={16} />} kleur="#7c3aed" label={t('Indicatieve marktprijs', 'Indicatieve marktprijs')}
                val={`${eur(mp.min)} – ${eur(mp.max)}`} sub={mp.toelichting} testid="pa-marktprijs" />}
              {vk && <Metric icon={<Clock size={16} />} kleur={vk.niveau === 'Hoog' ? '#16a34a' : vk.niveau === 'Laag' ? '#dc2626' : '#f59e0b'}
                label={t('Verkoopkans', 'Verkoopkans')} val={`${vk.niveau}${vk.score ? ` · ${vk.score}%` : ''}`} sub={vk.verkooptijd} testid="pa-verkoopkans" />}
              {winst && winst.vraagprijs > 0 && <Metric icon={<CheckCircle2 size={16} />} kleur="#0ea5e9"
                label={t('Verwachte winst', 'Verwachte winst')} val={eur(winst.verwachteWinst)} sub={`${t('Vraag', 'Vraag')} ${eur(winst.vraagprijs)}`} testid="pa-winst" />}
            </div>

            {/* Platform tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {PLATFORMS.map(p => (
                <button key={p.id} onClick={() => setPlatform(p.id)} data-testid={`pa-platform-${p.id}`}
                  className={`cs-btn ${platform === p.id ? 'primary' : ''}`} style={{ padding: '6px 12px', fontSize: 13 }}>{p.naam}</button>
              ))}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {['tekst', 'foto', 'prijs'].map(tt => (
                  <button key={tt} onClick={() => setTab(tt)} className={`cs-btn ${tab === tt ? 'primary' : ''}`}
                    data-testid={`pa-tab-${tt}`} style={{ padding: '6px 11px', fontSize: 12.5 }}>
                    {tt === 'tekst' ? t('Tekst', 'Tekst') : tt === 'foto' ? t('Foto\'s', 'Foto\'s') : t('Prijs', 'Prijs')}
                  </button>
                ))}
              </div>
            </div>

            {tab === 'tekst' && huidig && (
              <div data-testid="pa-tekst">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <strong style={{ fontSize: 14 }}>{huidig.titel}</strong>
                  <button onClick={() => kopieer(huidig.titel)} className="cs-btn" style={{ padding: '4px 9px', fontSize: 12 }} data-testid="pa-copy-titel"><Copy size={12} /> {t('Titel', 'Titel')}</button>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', background: 'var(--cs-bg-soft, #f8fafc)', border: '1px solid var(--cs-border)', borderRadius: 10, padding: 14, fontSize: 13.5, lineHeight: 1.6 }}>{huidig.tekst}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {onUseText && (
                    <button onClick={() => onUseText(`${huidig.titel}\n\n${huidig.tekst}`)} className="cs-btn primary" data-testid="pa-use-text"><CheckCircle2 size={14} /> {t('Gebruik deze tekst (OK)', 'Gebruik deze tekst (OK)')}</button>
                  )}
                  <button onClick={kopieerAlles} className={onUseText ? 'cs-btn' : 'cs-btn primary'} data-testid="pa-copy-alles"><Copy size={14} /> {t('Kopieer hele advertentie', 'Kopieer hele advertentie')}</button>
                  {platform === 'facebook' ? (
                    <button onClick={plaatsOpFacebook} className="cs-btn" style={{ background: '#1877f2', color: '#fff', borderColor: '#1877f2' }} data-testid="pa-facebook-plaats">
                      <ExternalLink size={14} /> {t('Plaats op Facebook', 'Plaats op Facebook')}
                    </button>
                  ) : (
                    <a className="cs-btn" href={marktplaatsUrl} target="_blank" rel="noreferrer" data-testid="pa-marktplaats-link"><ExternalLink size={14} /> {t('Vergelijk op Marktplaats', 'Vergelijk op Marktplaats')}</a>
                  )}
                  {fotos.length > 0 && (
                    <button onClick={downloadFotos} className="cs-btn" data-testid="pa-download-fotos"><Upload size={14} /> {t('Download foto\'s', 'Download foto\'s')} ({fotos.length})</button>
                  )}
                </div>
                {platform === 'facebook' && (
                  <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12, color: 'var(--cs-text-muted)', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 8, padding: '8px 10px', marginTop: 10 }}>
                    <AlertTriangle size={14} style={{ color: '#1877f2', flexShrink: 0, marginTop: 1 }} />
                    {t('Facebook staat automatisch plaatsen niet toe. Met deze knop kopiëren we de tekst, downloaden we je foto\'s en openen we Facebook Marketplace — daar plak je de tekst en sleep je de foto\'s erin.', 'Facebook staat automatisch plaatsen niet toe. Met deze knop kopiëren we de tekst, downloaden we je foto\'s en openen we Facebook Marketplace — daar plak je de tekst en sleep je de foto\'s erin.')}
                  </div>
                )}
              </div>
            )}

            {tab === 'foto' && (
              <div data-testid="pa-foto">
                <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginTop: 0 }}>
                  {t('Voeg foto\'s toe — de AI herkent de staat en kenmerken. Genereer opnieuw na het uploaden.', 'Voeg foto\'s toe — de AI herkent de staat en kenmerken. Genereer opnieuw na het uploaden.')}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {fotos.map((f, i) => (
                    <div key={i} style={{ position: 'relative' }} data-testid={`pa-foto-${i}`}>
                      <img src={f.dataUrl} alt="" style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--cs-border)' }} />
                      <button onClick={() => delFoto(i)} data-testid={`pa-foto-del-${i}`} style={{ position: 'absolute', top: -7, right: -7, width: 22, height: 22, borderRadius: '50%', background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} /></button>
                    </div>
                  ))}
                  {fotos.length < 6 && (
                    <button onClick={() => fileRef.current?.click()} data-testid="pa-foto-upload" style={{ width: 96, height: 72, border: '2px dashed var(--cs-border)', borderRadius: 8, background: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: 'var(--cs-text-muted)' }}>
                      <Upload size={18} /><span style={{ fontSize: 11 }}>{t('Upload', 'Upload')}</span>
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
                </div>
                {fa && (
                  <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: 12 }} data-testid="pa-fotoanalyse">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}><Camera size={15} /> {t('AI-fotoanalyse', 'AI-fotoanalyse')}</div>
                    {Array.isArray(fa.kenmerken) && fa.kenmerken.length > 0 && <div style={{ fontSize: 13, marginBottom: 4 }}>• {fa.kenmerken.join(' · ')}</div>}
                    {fa.staat && <div style={{ fontSize: 13 }}><strong>{t('Staat', 'Staat')}:</strong> {fa.staat}</div>}
                    {fa.schade && <div style={{ fontSize: 13 }}><strong>{t('Schade', 'Schade')}:</strong> {fa.schade}</div>}
                    {fa.waarde_invloed && <div style={{ fontSize: 13 }}><strong>{t('Invloed op waarde', 'Invloed op waarde')}:</strong> {fa.waarde_invloed}</div>}
                  </div>
                )}
              </div>
            )}

            {tab === 'prijs' && (
              <div data-testid="pa-prijs">
                {mp && <div style={{ fontSize: 14, marginBottom: 10 }}><strong>{t('Indicatieve marktprijs', 'Indicatieve marktprijs')}:</strong> {eur(mp.min)} – {eur(mp.max)}<div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginTop: 4 }}>{mp.toelichting}</div></div>}
                {result.beoordeling && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 13, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}><CheckCircle2 size={14} /> {t('Sterke punten', 'Sterke punten')}</div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>{(result.beoordeling.sterke_punten || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#f59e0b', fontSize: 13, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}><AlertTriangle size={14} /> {t('Verbeterpunten', 'Verbeterpunten')}</div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>{(result.beoordeling.verbeterpunten || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Publiceer in showroom */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--cs-border)', paddingTop: 14 }}>
              {showroom?.status === 'Gepubliceerd' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#16a34a', fontWeight: 600, fontSize: 13 }}><Globe size={15} /> {t('Staat live op je showroom', 'Staat live op je showroom')}</span>
              ) : (
                <button onClick={publiceer} disabled={publishing} className="cs-btn primary" data-testid="pa-publiceer"><Globe size={14} /> {t('Publiceer in showroom', 'Publiceer in showroom')}</button>
              )}
            </div>
          </>
        ) : (
          <div className="cs-empty">{t('Geen resultaat. Probeer opnieuw te genereren.', 'Geen resultaat. Probeer opnieuw te genereren.')}</div>
        )}
        <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );
}

const Metric = ({ icon, kleur, label, val, sub, testid }) => (
  <div data-testid={testid} style={{ background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 10, padding: 11 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: kleur, fontWeight: 700, fontSize: 12 }}>{icon} {label}</div>
    <div style={{ fontSize: 16, fontWeight: 800, marginTop: 3 }}>{val}</div>
    {sub && <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
  </div>
);
